import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Phone, Mail, MessageCircle, HelpCircle, Smartphone } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { useSeo, faqJsonLd } from "@/lib/seo";
import ApkDownloadButton from "@/components/ApkDownloadButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function HelpPage() {
  const nav = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ella.getHelpList()
      .then((res) => setFaqs(res.status ? res.data : []))
      .finally(() => setLoading(false));
  }, []);

  useSeo({
    title: "Bantuan & FAQ",
    description: "Pertanyaan yang sering ditanyakan tentang belanja frozen food di Ella Froze, kontak WhatsApp per cabang, dan aplikasi Android.",
    path: "/help",
    jsonLd: faqJsonLd(faqs),
  });

  return (
    <div className="pb-24">
      <div className="px-4 py-3 flex items-center gap-3 sticky top-[60px] bg-white z-30 border-b border-gray-100">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display text-lg font-bold text-gray-900">Bantuan</h1>
      </div>
      <div className="p-4 space-y-4">
        <div className="bg-gradient-to-br from-[#D32F2F] to-[#B71C1C] rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <HelpCircle className="w-8 h-8" />
            <div>
              <h2 className="font-display font-bold text-lg leading-tight">Pusat Bantuan Ella Froze</h2>
              <p className="text-xs text-white/90 mt-0.5">Temukan jawaban atas pertanyaan umummu di sini.</p>
            </div>
          </div>
        </div>

        <section data-testid="help-faq-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-2">Pertanyaan yang Sering Ditanyakan</h2>
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-12 skeleton rounded-xl" />)}</div>
          ) : faqs.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Belum ada FAQ</p>
          ) : (
            <Accordion type="single" collapsible className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {faqs.filter((f) => f.Content?.trim()).map((f) => (
                <AccordionItem key={f.ID} value={f.ID} className="border-b last:border-0 border-gray-100" data-testid={`faq-${f.ID}`}>
                  <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-left hover:no-underline">
                    {f.Title}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div
                      className="prose prose-sm max-w-none text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: f.Content }}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </section>

        <section data-testid="help-contact-section">
          <h2 className="font-display font-bold text-sm text-gray-900 mb-2">Hubungi Kami</h2>
          <div className="space-y-2">
            {[
              { icon: MessageCircle, label: "Chat Admin Cabang", value: "Buka halaman Pesan", href: "/messages", internal: true },
              { icon: Smartphone, label: "Aplikasi Android", value: "Unduh di Google Play", apk: true },
              { icon: Phone, label: "WhatsApp Jakarta", value: "0878-7766-2928", href: "https://wa.me/6287877662928" },
              { icon: Phone, label: "WhatsApp Yogyakarta", value: "0895-2989-9802", href: "https://wa.me/6289529899802" },
              { icon: Phone, label: "WhatsApp Pekalongan", value: "0819-3181-4005", href: "https://wa.me/6281931814005" },
              { icon: Phone, label: "WhatsApp Semarang", value: "0813-2820-0674", href: "https://wa.me/6281328200674" },
              { icon: Phone, label: "WhatsApp Cibubur", value: "0811-1255-707", href: "https://wa.me/628111255707" },
              { icon: Mail, label: "Email", value: "ellafroze.master@gmail.com", href: "mailto:ellafroze.master@gmail.com" },
            ].map(({ icon: Icon, label, value, href, internal, download, apk }) => {
              if (apk) {
                return (
                  <ApkDownloadButton
                    key={label}
                    label="Unduh Ella Froze di Google Play"
                    className="w-full bg-white rounded-2xl border border-gray-100 p-3 text-sm font-semibold text-gray-900 hover:shadow-sm"
                  />
                );
              }
              const Comp = internal ? "button" : "a";
              const props = internal
                ? { onClick: () => nav(href), type: "button" }
                : { href, download: download || undefined, target: (!download && href.startsWith("http")) ? "_blank" : undefined, rel: "noreferrer" };
              return (
                <Comp
                  key={label}
                  {...props}
                  className="w-full bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-3 hover:shadow-sm text-left"
                  data-testid={`help-contact-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#D32F2F]" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{value}</p>
                  </div>
                </Comp>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
